import { useState, createContext, useContext, useCallback } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useCurrentUser } from './hooks/useCurrentUser';
import { usePlaces } from './hooks/usePlaces';
import { useCountries } from './hooks/useCountries';
import { MapView } from './components/Map/MapView';
import { SearchBar } from './components/Search/SearchBar';
import { PlacePanel } from './components/PlacePanel/PlacePanel';
import { Sidebar } from './components/Sidebar/Sidebar';
import { LoadingSpinner } from './components/UI/LoadingSpinner';
import { ErrorBanner } from './components/UI/ErrorBanner';
import type { User, Place, NominatimResult } from './types';

async function reverseGeocode(lng: number, lat: number): Promise<NominatimResult | null> {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&zoom=16`;
    const res = await fetch(url, {
      headers: { 'User-Agent': 'WorldTravelTracker/1.0', 'Accept-Language': 'en' },
    });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

// ----- User Context -----
interface UserContextValue {
  user: User | null;
}
const UserContext = createContext<UserContextValue>({ user: null });
export function useUser() {
  return useContext(UserContext);
}

// ----- Main Map Page -----
function MapPage() {
  const { user, loading: userLoading, error: userError } = useCurrentUser();
  const { places, loading: placesLoading, refetch: refetchPlaces } = usePlaces();
  const { countries, refetch: refetchCountries } = useCountries('all');

  const [selectedPlace, setSelectedPlace] = useState<Place | null>(null);
  const [pendingNominatim, setPendingNominatim] = useState<NominatimResult | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [focusCountryCode, setFocusCountryCode] = useState<string | null>(null);

  // Compute available years from visited places
  const availableYears = Array.from(
    new Set(
      places
        .filter((p) => p.myVisitDate)
        .map((p) => new Date(p.myVisitDate!).getFullYear())
    )
  ).sort((a, b) => b - a);

  // Filter places by selected year
  const filteredPlaces = selectedYear
    ? places.filter((p) => p.myVisitDate && new Date(p.myVisitDate).getFullYear() === selectedYear)
    : places;

  // Derive countries from filtered places when year filter is active
  const filteredCountries = selectedYear
    ? Array.from(
        new Map(
          filteredPlaces
            .filter((p) => p.myVisitType && p.CountryCode)
            .map((p) => [p.CountryCode, { countryCode: p.CountryCode, visitType: p.myVisitType! }])
        ).values()
      )
    : countries;

  const handleSearchSelect = (result: NominatimResult) => {
    setPendingNominatim(result);
    setSelectedPlace(null);
  };

  const handlePlaceClick = (place: Place) => {
    setSelectedPlace(place);
    setPendingNominatim(null);
    setFocusCountryCode(null);
  };

  const handleCountryClick = (countryCode: string) => {
    setFocusCountryCode(countryCode);
    setSelectedPlace(null);
    setPendingNominatim(null);
  };

  const handleClose = () => {
    setSelectedPlace(null);
    setPendingNominatim(null);
  };

  const handleMapClick = useCallback(async (lng: number, lat: number) => {
    const result = await reverseGeocode(lng, lat);
    if (result) {
      setPendingNominatim(result);
      setSelectedPlace(null);
    }
  }, []);

  const handleVisitAdded = () => {
    refetchPlaces();
    refetchCountries();
  };

  if (userLoading) return <LoadingSpinner />;
  if (userError) return <ErrorBanner message={userError} />;

  const signedIn = !!user;

  return (
    <UserContext.Provider value={{ user }}>
      <div style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden' }}>

        {/* Top bar */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 54,
            background: 'rgba(255,255,255,0.95)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '0 16px',
            zIndex: 15,
          }}
        >
          <span style={{ fontSize: 20 }}>🌍</span>
          <span style={{ fontWeight: 700, fontSize: 16, marginRight: 16, whiteSpace: 'nowrap' }}>
            World Travel
          </span>
          {signedIn && <SearchBar onSelect={handleSearchSelect} />}
          {signedIn && placesLoading && <LoadingSpinner size={20} />}
          <div style={{ marginLeft: 'auto' }}>
            {signedIn ? (
              <a
                href="/.auth/logout"
                style={{
                  fontSize: 13,
                  color: '#9e9e9e',
                  textDecoration: 'none',
                  padding: '6px 12px',
                  border: '1px solid #e0e0e0',
                  borderRadius: 6,
                }}
              >
                Sign out
              </a>
            ) : (
              <a
                href="/.auth/login/aad"
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#fff',
                  textDecoration: 'none',
                  padding: '8px 18px',
                  background: '#2196F3',
                  borderRadius: 6,
                }}
              >
                Sign in
              </a>
            )}
          </div>
        </div>

        {/* Left sidebar — only when signed in */}
        {signedIn && (
          <Sidebar
            user={user}
            places={filteredPlaces}
            countries={filteredCountries}
            onPlaceClick={handlePlaceClick}
            onCountryClick={handleCountryClick}
            availableYears={availableYears}
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
          />
        )}

        {/* Map */}
        <div style={{ position: 'absolute', top: 54, left: 0, right: 0, bottom: 0 }}>
          <MapView
            places={filteredPlaces}
            countries={filteredCountries}
            focusPlace={selectedPlace}
            focusCountryCode={focusCountryCode}
            onPlaceClick={signedIn ? handlePlaceClick : undefined}
            onMapClick={signedIn ? handleMapClick : undefined}
          />
        </div>

        {/* Right panel — only when signed in */}
        {signedIn && (
          <PlacePanel
            selectedPlace={selectedPlace}
            pendingNominatim={pendingNominatim}
            onClose={handleClose}
            onVisitAdded={handleVisitAdded}
          />
        )}
      </div>
    </UserContext.Provider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<MapPage />} />
        <Route path="*" element={<MapPage />} />
      </Routes>
    </BrowserRouter>
  );
}
